// IncidentPages.tsx — /liff/incident/new + /liff/incident/my
//
// /liff/incident/new is the most-used flow on the platform: officer or
// incident_reporter taps "Report incident" from the Rich Menu, the LIFF
// page opens inside LINE, and they capture (severity, site, title,
// description, optional photo). On submit we POST to patrol-api which
// idempotently creates a row in nexus_event_*.incidents and triggers a
// daily-summary notification to the manager. On network failure the form
// draft is kept in-memory so a retry attempt does not lose the typed text.
//
// /liff/incident/my lists the incidents this LINE user has filed, ordered
// newest-first.

import React, { useEffect, useMemo, useState } from "react";
import { useLiff } from "../../../liff/index.js";
import { apiFetch } from "../../../lib/api-client.js";
import {
  MobileShell, Card, CardHeader, MobileSpinner, EmptyMobile, ComingSoonCard,
  PrimaryButton, SecondaryButton, SeverityPill,
} from "../../../liff/components/Mobile.js";
import { formatTaipeiDateTime } from "../../../lib/time.js";

type Severity = "info" | "low" | "med" | "high" | "critical";

interface DraftState {
  site: string;
  title: string;
  description: string;
  severity: Severity;
}

const EMPTY_DRAFT: DraftState = { site: "", title: "", description: "", severity: "med" };

/** /liff/incident/new — capture + submit. */
export function IncidentNewPage(): React.ReactElement {
  const { session } = useLiff();
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{ id: string } | null>(null);

  const canSubmit = useMemo(() => draft.title.trim().length >= 4 && !submitting, [draft, submitting]);

  async function submit(): Promise<void> {
    if (!session || !canSubmit) return;
    setSubmitError(null);
    setSubmitting(true);
    const idempotencyKey = `${session.user_id ?? session.profile.line_user_id}-${Date.now()}`;
    try {
      const r = await apiFetch(
        `/v1/incidents`,
        {
          method: "POST",
          headers: {
            "content-type": "application/json",
            "x-idempotency-key": idempotencyKey,
          },
          body: JSON.stringify({
            tenant_id: session.tenant_id,
            site_label: draft.site || null,
            title: draft.title.trim(),
            description: draft.description.trim() || null,
            severity: draft.severity,
            source: "liff_incident_new",
          }),
        },
        { jwt: session.session_token },
      );
      if (r.ok) {
        const body = await r.json() as { id?: string };
        setSubmitted({ id: body.id ?? idempotencyKey });
      } else {
        const text = await r.text().catch(() => `HTTP ${r.status}`);
        setSubmitError(text.slice(0, 240));
      }
    } catch (e) {
      // Offline / network error — keep the draft and tell the user.
      setSubmitError((e as Error).message ?? "network_error");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <MobileShell title="提交異常" subtitle="已送出">
        <Card>
          <CardHeader title="提交成功" hint={submitted.id.slice(0, 12)} />
          <p className="text-sm text-text-secondary leading-relaxed mb-3">
            您的通報已建立，相關主管將收到 LINE 通知。
          </p>
          <PrimaryButton onClick={() => { setSubmitted(null); setDraft(EMPTY_DRAFT); }}>再通報一筆</PrimaryButton>
          <div className="h-2" />
          <SecondaryButton href="/liff/incident/my">查看我的通報</SecondaryButton>
        </Card>
      </MobileShell>
    );
  }

  return (
    <MobileShell title="提交異常" subtitle="拍照 + 標題 + 嚴重度">
      <Card>
        <Field label="嚴重度">
          <div className="grid grid-cols-5 gap-1.5">
            {(["info", "low", "med", "high", "critical"] as Severity[]).map((s) => {
              const active = draft.severity === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, severity: s }))}
                  className={`h-10 rounded-lg text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                    active ? "bg-accent text-text-inverse" : "bg-bg-raised text-text-secondary"
                  }`}
                >
                  <SeverityPill severity={s} />
                </button>
              );
            })}
          </div>
        </Field>
        <Field label="案場 / 位置">
          <input
            type="text"
            value={draft.site}
            onChange={(e) => setDraft((d) => ({ ...d, site: e.target.value }))}
            placeholder="例: A 案場 後門"
            className="h-12 w-full rounded-xl bg-bg-raised px-3 text-sm placeholder:text-text-muted text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40"
            inputMode="text"
            autoComplete="off"
            maxLength={120}
          />
        </Field>
        <Field label="標題（必填）">
          <input
            type="text"
            value={draft.title}
            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            placeholder="一句話描述"
            className="h-12 w-full rounded-xl bg-bg-raised px-3 text-sm placeholder:text-text-muted text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40"
            maxLength={120}
          />
        </Field>
        <Field label="描述">
          <textarea
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            placeholder="可附時間、人物、設備代號…"
            rows={5}
            className="w-full rounded-xl bg-bg-raised px-3 py-2 text-sm placeholder:text-text-muted text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40"
            maxLength={2000}
          />
        </Field>
        {submitError && (
          <p className="text-xs text-danger bg-danger/10 rounded-lg p-3 mb-3">{submitError}</p>
        )}
        <PrimaryButton onClick={submit} disabled={!canSubmit}>
          {submitting ? "送出中…" : "送出通報"}
        </PrimaryButton>
      </Card>

      <p className="text-[10px] text-text-muted text-center">
        通報內容會傳給該案場的主管。緊急狀況請另行撥打 110 / 119。
      </p>
    </MobileShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className="mb-3 last:mb-0">
      <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">{label}</label>
      {children}
    </div>
  );
}

interface MyIncidentRow {
  id: string;
  title: string;
  severity: Severity;
  status: "open" | "ack" | "resolved";
  created_at_local: string;
  site_name?: string;
}

interface MyIncidentsResponse {
  items: MyIncidentRow[];
}

/** /liff/incident/my — list of my submissions. */
export function IncidentMyPage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<MyIncidentsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    apiFetch(`/v1/incidents/my?tenant_id=${encodeURIComponent(session.tenant_id)}`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) setData(await r.json() as MyIncidentsResponse);
        else setError(`HTTP ${r.status}`);
      })
      .catch((e) => setError((e as Error).message));
  }, [session]);

  if (error) return (
    <MobileShell title="我的通報"><Card><p className="text-sm text-danger">無法載入：{error}</p></Card></MobileShell>
  );
  if (!data) return <MobileSpinner message="正在載入您的通報" />;

  return (
    <MobileShell title="我的通報" subtitle={`${data.items.length} 筆`}>
      <Card>
        <CardHeader title="提交紀錄" />
        {data.items.length === 0 ? (
          <EmptyMobile
            title="您還沒有提交過通報"
            body="點擊下方按鈕提交第一筆。"
          />
        ) : (
          <ul className="space-y-2">
            {data.items.map((it) => {
              const dt = new Date(it.created_at_local);
              const local = isNaN(dt.getTime())
                ? it.created_at_local
                : formatTaipeiDateTime(dt);
              const statusLabel = it.status === "open" ? "待處理" : it.status === "ack" ? "已確認" : "已結案";
              const statusTone = it.status === "open" ? "bg-warning/15 text-warning"
                : it.status === "ack" ? "bg-accent/15 text-accent"
                : "bg-success/15 text-success";
              return (
                <li key={it.id} className="rounded-xl bg-bg-raised p-3">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-medium truncate flex-1">{it.title}</p>
                    <SeverityPill severity={it.severity} />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] text-text-muted truncate">
                      {it.site_name ?? "—"} · {local}
                    </p>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${statusTone}`}>
                      {statusLabel}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className="h-3" />
        <PrimaryButton href="/liff/incident/new">通報新異常</PrimaryButton>
      </Card>
    </MobileShell>
  );
}
